import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { UserService, LandingService } from "@/lib/db";
import { useAuth } from "@/contexts/AuthContext";

interface TermsModalProps {
    open: boolean;
    onAccept: () => void;
}

export function TermsModal({ open, onAccept }: TermsModalProps) {
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [scrolledToBottom, setScrolledToBottom] = useState(false);
    const [termsContent, setTermsContent] = useState("");
    const [termsVersion, setTermsVersion] = useState("1.0");
    const [contentLoading, setContentLoading] = useState(true);

    useEffect(() => {
        if (open) {
            const fetchTerms = async () => {
                const content = await LandingService.getLandingContent();
                if (content) {
                    setTermsContent(content.termsContent || "");
                    setTermsVersion(content.termsVersion || "1.0");
                }
                setContentLoading(false);
            };
            fetchTerms();
        }
    }, [open]);

    const handleAccept = async () => {
        if (!user) return;
        setLoading(true);
        try {
            await UserService.acceptTerms(user.uid, termsVersion);
            onAccept();
        } catch (error) {
            console.error("Failed to accept terms:", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open}>
            <DialogContent className="sm:max-w-[600px] h-[80vh] flex flex-col [&>button]:hidden" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
                <DialogHeader>
                    <DialogTitle>Terms of Service & Privacy Policy</DialogTitle>
                    <DialogDescription>
                        Version {termsVersion} • Please read and accept our terms to continue using Traskr.
                    </DialogDescription>
                </DialogHeader>

                <ScrollArea
                    className="flex-1 border rounded-md p-4 mt-4"
                    onScroll={(e: any) => {
                        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
                        if (scrollHeight - scrollTop <= clientHeight + 10) {
                            setScrolledToBottom(true);
                        }
                    }}
                >
                    {contentLoading ? (
                        <div className="flex items-center justify-center h-full">Loading terms...</div>
                    ) : (
                        <div className="prose prose-sm" dangerouslySetInnerHTML={{ __html: termsContent || "<p>Standard Terms and Conditions apply.</p>" }} />
                    )}
                </ScrollArea>

                <DialogFooter className="mt-4 flex flex-col sm:flex-row gap-2">
                    <div className="text-xs text-muted-foreground flex-1 flex items-center">
                        {scrolledToBottom ? "Thank you for reading." : "Please scroll to the end to accept."}
                    </div>
                    <Button onClick={handleAccept} disabled={loading} className="w-full sm:w-auto">
                        {loading ? "Signing..." : "I Accept the Terms"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
